import { IconBorderText } from '@components/quick-help';
import { ArrowRightTwoIcon, ChevronRightIcon, CloseIcon, SettingsIcon } from '@sketchnote/icons';
import Tippy from '@tippyjs/react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import * as React from 'react';
import { PLANRULES } from 'src/constants/plans';
import { boardViews, pageViews } from 'src/features/files/assets/data';
import { useFileAccess } from 'src/features/files/hooks/useFileAccess';
import { useAuth } from 'src/shared/hooks/use-auth';

import { useFileExploreStore } from '../store';
import { FileActions } from './file-actions';
import { PDFDownload } from './pdf-download';
import { SlackButton } from './slackbutton';
import { VersionHistoryButton } from './version-history-button';

export const Home = (): JSX.Element => {
  const { closeExploreMenu, setNavigation, setIsSaveTemplateOpen } = useFileExploreStore();
  const router = useRouter();
  const authData = useAuth((s) => s.userDetails);

  const isSketchnoteMember =
    authData?.email.endsWith('sketchnote.co') ||
    authData?.email === 'placebonikhil@gmail.com' ||
    authData?.email === 'ajaymenon0@gmail.com';
  const type = router.query.type as string;
  const handleOnClose = () => {
    closeExploreMenu();
  };
  const handleNavigateTo = (value) => {
    setNavigation(value);
  };

  const { isReadOnly } = useFileAccess();

  const isExportRestricted = authData?.plan.type ? !PLANRULES.export[authData?.plan.type] : false;

  const isVersionHistoryRestricted = authData?.plan.type
    ? !PLANRULES.versionhistory[authData?.plan.type]
    : false;

  return (
    <div className="h-full flex flex-col justify-between">
      <div className="">
        <ExploreHeader title="Explore" onCloseClick={handleOnClose} />

        {boardViews.includes(type) &&
          navigations.map(({ label, value, icon, isDisabled }) => (
            <IconBorderText
              text={label}
              key={value}
              icon={icon}
              onClick={!isDisabled ? handleNavigateTo.bind(this, value) : undefined}
            />
          ))}
        <div className="ml-10">
          {type === 'form' && (
            <IconBorderText
              text="Form settings"
              leftIcon={<SettingsIcon />}
              icon={<ChevronRightIcon />}
              leftIconClass="pt-2"
              onClick={handleNavigateTo.bind(this, 'form-settings')}
            />
          )}
        </div>

        <div className="border-b">
          <FileActions />
        </div>

        <Tippy
          content="You do not have the required permission to create templates"
          disabled={!isReadOnly}
        >
          <button
            onClick={() => {
              if (isReadOnly) return;
              setIsSaveTemplateOpen(true);
              closeExploreMenu();
            }}
            className={classNames(
              'pl-8 flex py-20 justify-between text-gray-600 text-sm items-center w-full border-gray-200 hover:text-gray-800 cursor-pointer',
              {
                'opacity-50 cursor-not-allowed': isReadOnly,
              }
            )}
          >
            <span>↓&nbsp;Save as template</span>
          </button>
        </Tippy>
        {router.query.type === 'document' && (
          <VersionHistoryButton disabled={isVersionHistoryRestricted} />
        )}

        {boardViews.includes(type) && <SlackButton />}
      </div>
      <div>{router.query.type === 'document' && <PDFDownload disabled={isExportRestricted} />}</div>
    </div>
  );
};

export const navigations: {
  label: string;
  value: string;
  icon: JSX.Element;
  isDisabled?: boolean;
}[] = [
  {
    label: '📲 &nbsp; Integrations',
    value: 'integrations',
    icon: <ChevronRightIcon />,
    isDisabled: false,
  },
  // {
  //   label: '💼 &nbsp; Task templates',
  //   value: 'task-templates',
  //   icon: <ChevronRightIcon />,
  //   isDisabled: false,
  // },
];

export const ExploreHeader = ({
  title,
  onCloseClick,
  onBackClick,
}: {
  title?: string;
  onCloseClick?: () => void;
  onBackClick?: () => void;
}): JSX.Element => (
  <div className="flex justify-between items-center py-16 mb-5">
    <h6 className="text-base flex items-center">
      {onBackClick ? (
        <button onClick={onBackClick}>
          <ArrowRightTwoIcon wrapperClass="text-gray-500 transform rotate-180 mr-16" />
          {title}
        </button>
      ) : (
        title
      )}
    </h6>
    <button onClick={onCloseClick}>
      <CloseIcon className="text-gray-500 text-xs" />
    </button>
  </div>
);
